import { useCallback, useEffect, useRef, useState } from "react"

export default function useHorizontalScroll<T extends HTMLElement = HTMLDivElement>() {
	const ref = useRef<T>(null)
	const [overflow, setOverflow] = useState({ start: false, end: false })

	useEffect(() => {
		const element = ref.current
		if (!element) return

		const update = () => {
			const remaining = element.scrollWidth - element.clientWidth - element.scrollLeft
			setOverflow({ start: element.scrollLeft > 1, end: remaining > 1 })
		}
		update()

		element.addEventListener("scroll", update, { passive: true })
		const resizeObserver = new ResizeObserver(update)
		resizeObserver.observe(element)
		const mutationObserver = new MutationObserver(update)
		mutationObserver.observe(element, { childList: true, subtree: true, characterData: true })

		return () => {
			element.removeEventListener("scroll", update)
			resizeObserver.disconnect()
			mutationObserver.disconnect()
		}
	}, [])

	const scroll = useCallback((direction: "start" | "end") => {
		const element = ref.current
		if (!element) return

		element.scrollBy({ left: (direction === "start" ? -1 : 1) * element.clientWidth * 0.8, behavior: "smooth" })
	}, [])

	return { ref, canScrollToStart: overflow.start, canScrollToEnd: overflow.end, scroll }
}
